import React, { useEffect, useRef } from 'react';

// A sablon ablak-szélességei: 480 alap, 560 széles, 720 extra széles.
const WIDTHS = {
  sm: 'max-w-[400px]',
  md: 'max-w-[480px]',
  lg: 'max-w-[560px]',
  xl: 'max-w-[640px]',
  '2xl': 'max-w-[720px]',
  '5xl': 'max-w-5xl',   // széles párbeszéd (pl. a changelog importja)
};

const TONES = {
  brand:  'bg-brand-500/[0.12] text-brand-500 shadow-[inset_0_0_0_1px_rgba(164,139,255,0.30)]',
  danger: 'bg-danger/10 text-danger shadow-[inset_0_0_0_1px_rgba(240,86,74,0.30)]',
  warn:   'bg-warn/10 text-warn shadow-[inset_0_0_0_1px_rgba(229,180,84,0.30)]',
};

/**
 * Közös ablak-váz — a jóváhagyott sablon „window” ablaka (2026-10-04): elmosott,
 * sötétített háttér; panel-alapú lap 10 px sarokkal; fejléc-sáv cím + alcím + bezárás.
 *
 * A háttérre kattintás és az Escape egyaránt zár. A háttér-kattintás csak akkor
 * zár, ha a lenyomás ÉS a felengedés is a lapon kívül volt: egy a lapon kezdett
 * kijelölés, amit kint engednek fel, a böngészőben `click` a közös szülőn — a
 * háttéren — és enélkül bezárta az ablakot a beírt szöveggel együtt.
 */
export default function Modal({
  title,
  subtitle,
  icon,
  tone = 'brand',
  size = 'md',
  onClose,
  children,
  footer,
  closeOnBackdrop = true,
}) {
  const panelRef = useRef(null);
  const downOutside = useRef(false);
  const upOutside = useRef(false);
  const outside = (e) => !panelRef.current || !panelRef.current.contains(e.target);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);

    // A háttér ne görögjön az ablak alatt.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[240] flex items-center justify-center px-4 py-6 animate-fade-in
                 bg-[rgba(5,5,7,0.72)] backdrop-blur-[4px]"
      onMouseDown={e => { downOutside.current = outside(e); }}
      onMouseUp={e => { upOutside.current = outside(e); }}
      onClick={() => {
        const close = downOutside.current && upOutside.current;
        downOutside.current = upOutside.current = false;
        if (closeOnBackdrop && close) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={panelRef}
        onClick={e => e.stopPropagation()}
        className={`window relative w-full ${WIDTHS[size] || WIDTHS.md} max-h-[calc(100vh-48px)] overflow-y-auto animate-toast-in`}
      >
        {(title || onClose) && (
          <div className="min-h-[56px] py-3 pl-5 pr-3 flex items-center gap-3 border-b border-line-faint">
            {icon && (
              <span className={`flex-shrink-0 w-9 h-9 rounded-[9px] grid place-items-center ${TONES[tone] || TONES.brand}`}>
                {icon}
              </span>
            )}
            <div className="flex-1 min-w-0">
              {title && <h3 className="text-[15px] font-semibold text-gray-50">{title}</h3>}
              {subtitle && <p className="text-[12.5px] text-gray-500">{subtitle}</p>}
            </div>
            {onClose && (
              <button type="button" onClick={onClose} className="btn-icon w-8 h-8 flex-shrink-0" aria-label="Close">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
                     strokeLinecap="round" className="w-4 h-4" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}

        {children && <div className="p-5">{children}</div>}

        {footer && (
          <div className="px-5 py-3.5 border-t border-line-faint">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
